import { useEffect, useMemo, useState } from 'react';
import { BarChart3, ChevronDown, ChevronUp, Filter, Info, TrendingDown, TrendingUp, X } from 'lucide-react';
import type { StatTrend } from '../../api/planets';

interface StatsPanelProps {
  stats?: Record<string, number>;
  geography?: {
    landArea: number;
    waterCoverage: number;
    usableLand: number;
    usedLand: number;
  };
  lifeChance?: number;
  statTrends?: Record<string, StatTrend>;
}

const STAT_DEFS = [
  { label: 'Atmosphere', key: 'atmosphere', max: 100, color: 'bg-sky-400', stage: 0, unit: '%' },
  { label: 'Water', key: 'water', max: 100, color: 'bg-teal-500', stage: 0, unit: '%' },
  { label: 'Temp', key: 'temperature', max: 100, color: 'bg-orange-400', stage: 0, unit: 'C', offset: -35 },
  { label: 'Stability', key: 'stability', max: 100, color: 'bg-stone-400', stage: 0, unit: '%' },
  { label: 'Oxygen', key: 'oxygen', max: 100, color: 'bg-cyan-400', stage: 1, unit: '%' },
  { label: 'Life', key: 'primitiveLife', max: 100, color: 'bg-green-400', stage: 2, unit: '%' },
  { label: 'Biomass', key: 'biomass', max: 100, color: 'bg-emerald-500', stage: 2, unit: '%' },
  { label: 'Biodiversity', key: 'biodiversity', max: 100, color: 'bg-teal-400', stage: 2, unit: '%' },
  { label: 'Animals', key: 'animalLife', max: 100, color: 'bg-lime-400', stage: 4, unit: '%' },
  { label: 'Population', key: 'population', max: 100, color: 'bg-yellow-400', stage: 5, unit: '%' },
  { label: 'Tech', key: 'technology', max: 100, color: 'bg-purple-400', stage: 6, unit: '%' },
  { label: 'Energy', key: 'energy', max: 100, color: 'bg-amber-400', stage: 5, unit: '%' },
  { label: 'Research', key: 'researchSpeed', max: 100, color: 'bg-violet-300', stage: 5, unit: '%' },
  { label: 'Mapped', key: 'scanRange', max: 100, color: 'bg-indigo-400', stage: 6, unit: '%' },
];

type StatDef = typeof STAT_DEFS[number];

const DETAIL_LABELS: Record<string, string> = {
  landArea: 'Surface',
  waterCoverage: 'Ocean',
  usableLand: 'Usable Land',
  lifeChance: 'Life Chance',
  pollution: 'Pollution',
};

function formatDelta(value: number) {
  if (Math.abs(value) < 0.05) return '0.0';
  return `${value > 0 ? '+' : ''}${value.toFixed(1)}`;
}

function formatStatValue(value: number, stat: StatDef): string {
  const raw = typeof value === 'number' ? value : 0;
  if (stat.unit === 'C' && stat.offset !== undefined) {
    return `${Math.round(raw + stat.offset)}C`;
  }
  return `${Math.round(raw)}${stat.unit}`;
}

function trendIcon(trend?: StatTrend['trend']) {
  if (trend === 'up') return <TrendingUp className="h-3 w-3 text-green-400" />;
  if (trend === 'down') return <TrendingDown className="h-3 w-3 text-red-400" />;
  return <Info className="h-3 w-3 text-slate-500" />;
}

function sourceLabel(sourceType: string) {
  if (sourceType === 'project_daily') return 'Daily project';
  if (sourceType === 'project_completion') return 'Completion';
  if (sourceType === 'natural') return 'Natural';
  if (sourceType === 'planet_balance') return 'Balance';
  return 'Change';
}

function StatButton({
  label,
  value,
  progress,
  color,
  trend,
  onClick,
}: {
  label: string;
  value: string;
  progress: number;
  color: string;
  trend?: StatTrend;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="min-w-[82px] rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-left shadow-lg shadow-black/30 transition hover:border-cyan-700 hover:bg-slate-900 focus:outline-none focus:ring-1 focus:ring-cyan-500"
      aria-label={`Show ${label} details`}
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-xs text-slate-400">{label}</span>
        <span className="flex items-center gap-1 text-xs font-mono text-slate-100">
          {value}
          {trendIcon(trend?.trend)}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
        <div className={`h-full rounded-full ${color} transition-all duration-500`} style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} />
      </div>
    </button>
  );
}

export default function StatsPanel({ stats, geography, lifeChance, statTrends = {} }: StatsPanelProps) {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('planetseed.statsCollapsed') === 'true';
  });
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [hiddenStats, setHiddenStats] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set();
    const raw = window.localStorage.getItem('planetseed.hiddenStats');
    return new Set(raw ? JSON.parse(raw) : []);
  });

  useEffect(() => {
    window.localStorage.setItem('planetseed.statsCollapsed', String(collapsed));
    if (collapsed) setSelectedKey(null);
  }, [collapsed]);

  useEffect(() => {
    window.localStorage.setItem('planetseed.hiddenStats', JSON.stringify([...hiddenStats]));
  }, [hiddenStats]);

  // Auto-hide stats that reach 100%
  useEffect(() => {
    const newlyMaxed = STAT_DEFS.filter((s) => {
      const val = stats?.[s.key] ?? 0;
      return val >= 100 && !hiddenStats.has(s.key);
    }).map((s) => s.key);
    if (newlyMaxed.length > 0) {
      setHiddenStats((prev) => new Set([...prev, ...newlyMaxed]));
    }
  }, [stats]);

  const visibleStats = STAT_DEFS.filter((s) => (stats?.[s.key] ?? 0) > 0);
  const showGeography = geography && (geography.landArea > 0 || geography.waterCoverage > 0);

  const selected = useMemo(() => {
    if (!selectedKey) return null;
    const stat = STAT_DEFS.find((item) => item.key === selectedKey);
    if (stat) {
      const value = stats?.[stat.key] ?? 0;
      return {
        key: stat.key,
        label: stat.label,
        value: formatStatValue(value, stat),
        trend: statTrends[stat.key],
      };
    }
    if (selectedKey === 'landArea') {
      return { key: selectedKey, label: 'Surface', value: `${Math.round(100 - (geography?.waterCoverage || 0))}%`, trend: statTrends.landArea };
    }
    if (selectedKey === 'waterCoverage') {
      return { key: selectedKey, label: 'Ocean', value: `${Math.round(geography?.waterCoverage || 0)}%`, trend: statTrends.waterCoverage };
    }
    if (selectedKey === 'usableLand') {
      return { key: selectedKey, label: 'Usable Land', value: `${Math.round(geography?.usableLand || 0)}%`, trend: statTrends.usableLand };
    }
    if (selectedKey === 'lifeChance') {
      return { key: selectedKey, label: 'Life Chance', value: `${Math.round(lifeChance || 0)}%`, trend: statTrends.lifeChance };
    }
    return {
      key: selectedKey,
      label: DETAIL_LABELS[selectedKey] || selectedKey,
      value: '',
      trend: statTrends[selectedKey],
    };
  }, [selectedKey, stats, statTrends, geography, lifeChance]);

  const n2 = stats?.nitrogen ?? 0;
  const o2 = stats?.oxygen ?? 0;
  const co2 = stats?.carbon ?? 0;
  const totalChem = n2 + o2 + co2 || 1;

  return (
    <div className="absolute bottom-3 left-3 right-3 z-20 pointer-events-none sm:bottom-4 sm:left-4 sm:right-4">
      <div className="pointer-events-auto flex flex-col items-center gap-2">
        {collapsed ? (
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            className="flex items-center gap-2 rounded-full border border-cyan-800 bg-slate-950 px-3 py-2 text-xs font-semibold text-cyan-200 shadow-xl shadow-black/40 hover:bg-slate-900 focus:outline-none focus:ring-1 focus:ring-cyan-500"
            aria-label="Show planet stats"
          >
            <BarChart3 className="h-4 w-4" />
            Stats
            <ChevronUp className="h-4 w-4" />
          </button>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCollapsed(true)}
                className="flex items-center gap-1.5 rounded-full border border-slate-800 bg-slate-950 px-3 py-1.5 text-xs font-semibold text-slate-300 shadow-lg shadow-black/30 hover:border-cyan-800 hover:text-cyan-200 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                aria-label="Collapse planet stats"
              >
                <BarChart3 className="h-3.5 w-3.5" />
                Stats
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setFilterOpen((v) => !v)}
                className="flex items-center gap-1 rounded-full border border-slate-700 bg-slate-950 px-2.5 py-1.5 text-xs text-slate-400 hover:text-cyan-200 shadow-lg shadow-black/30"
                aria-label="Toggle stat visibility"
              >
                <Filter className="h-3 w-3" />
                Filter
              </button>
            </div>

            {filterOpen && (
              <div className="w-full max-w-xs rounded-lg border border-slate-700 bg-slate-950 p-2.5 shadow-xl">
                <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Show / Hide Stats</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {STAT_DEFS.filter((s) => (stats?.[s.key] ?? 0) > 0).map((stat) => (
                    <label key={stat.key} className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!hiddenStats.has(stat.key)}
                        onChange={(e) => {
                          setHiddenStats((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.delete(stat.key);
                            else next.add(stat.key);
                            return next;
                          });
                        }}
                        className="rounded border-slate-600"
                      />
                      {stat.label}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {showGeography && (
              <div className="flex flex-wrap justify-center gap-2">
                <StatButton
                  label="Surface"
                  value={`${Math.round(100 - geography.waterCoverage)}%`}
                  progress={100 - geography.waterCoverage}
                  color="bg-amber-600"
                  trend={statTrends.landArea}
                  onClick={() => setSelectedKey('landArea')}
                />
                <StatButton
                  label="Ocean"
                  value={`${Math.round(geography.waterCoverage)}%`}
                  progress={geography.waterCoverage}
                  color="bg-teal-600"
                  trend={statTrends.waterCoverage}
                  onClick={() => setSelectedKey('waterCoverage')}
                />
                <StatButton
                  label="Usable"
                  value={`${Math.round(geography.usableLand)}%`}
                  progress={geography.usableLand}
                  color="bg-lime-500"
                  trend={statTrends.usableLand}
                  onClick={() => setSelectedKey('usableLand')}
                />
              </div>
            )}

            {typeof lifeChance === 'number' && lifeChance > 0 && (
              <button
                type="button"
                onClick={() => setSelectedKey('lifeChance')}
                className={`rounded-full border px-3 py-1 text-xs font-semibold shadow-lg shadow-black/30 ${
                  lifeChance >= 60
                    ? 'border-green-700 bg-green-950 text-green-300'
                    : lifeChance >= 30
                      ? 'border-yellow-700 bg-yellow-950 text-yellow-300'
                      : 'border-red-700 bg-red-950 text-red-300'
                }`}
              >
                Life Chance: {Math.round(lifeChance)}%
              </button>
            )}

            <div className="flex max-h-[34vh] flex-wrap justify-center gap-2 overflow-y-auto px-1 pb-1 sm:max-h-none">
              {visibleStats.filter((s) => !hiddenStats.has(s.key)).map((stat) => {
                const value = stats?.[stat.key] ?? 0;
                return (
                  <StatButton
                    key={stat.key}
                    label={stat.label}
                    value={formatStatValue(value, stat)}
                    progress={(value / stat.max) * 100}
                    color={stat.color}
                    trend={statTrends[stat.key]}
                    onClick={() => setSelectedKey(stat.key)}
                  />
                );
              })}
            </div>
          </>
        )}

        {selected && !collapsed && (
          <div className="w-full max-w-sm rounded-lg border border-cyan-800 bg-slate-950 p-3 text-xs shadow-2xl shadow-black/70">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 font-semibold text-slate-100">
                  {selected.label}
                  {trendIcon(selected.trend?.trend)}
                </div>
                <div className="mt-0.5 text-slate-500">
                  Current {selected.value || 'value'} - Net {formatDelta(selected.trend?.netChange || 0)} next day
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedKey(null)}
                className="rounded-md p-1 text-slate-500 hover:bg-slate-900 hover:text-slate-200"
                aria-label="Close stat details"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-3 space-y-1.5">
              {(selected.trend?.reasons || []).length > 0 ? (
                selected.trend?.reasons.map((reason, index) => (
                  <div key={`${reason.label}-${index}`} className="flex items-center justify-between gap-3 rounded-md bg-slate-900 px-2 py-1.5">
                    <div className="min-w-0">
                      <div className="truncate text-slate-300">{reason.label}</div>
                      <div className="text-[10px] text-slate-500">{sourceLabel(reason.sourceType)}</div>
                    </div>
                    <span className={`font-mono ${reason.delta > 0 ? 'text-green-400' : reason.delta < 0 ? 'text-red-400' : 'text-slate-500'}`}>
                      {formatDelta(reason.delta)}
                    </span>
                  </div>
                ))
              ) : (
                <div className="rounded-md bg-slate-900 px-2 py-2 text-slate-400">
                  No meaningful daily change is predicted right now.
                </div>
              )}
            </div>

            {selected.key === 'atmosphere' && (
              <div className="mt-3 border-t border-slate-800 pt-3">
                <div className="mb-2 font-semibold text-slate-200">Composition</div>
                {[
                  ['Nitrogen', n2, 'bg-blue-400'],
                  ['Oxygen', o2, 'bg-cyan-400'],
                  ['Carbon', co2, 'bg-amber-400'],
                ].map(([label, raw, color]) => {
                  const value = Number(raw);
                  return (
                    <div key={String(label)} className="mb-1 flex items-center justify-between gap-2">
                      <span className="text-slate-400">{label}</span>
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-800">
                          <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(100, (value / totalChem) * 100)}%` }} />
                        </div>
                        <span className="w-8 text-right font-mono text-slate-300">{Math.round(value)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
